import { IsolatedCartType, getBuyNowKey } from '@teko/cart/v2';
import { DiscoveryProduct } from '@teko/discovery';
import { ShoppingCartAddColored } from '@teko/ds-icon';
import {
  BaseProps,
  Body,
  Button,
  Caption,
  Col,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalHeader,
  Quantity,
  Row,
  Shape,
  SubTitle,
  styled,
  useTheme,
  useToast,
} from '@teko/ds-mobile';
import { logger } from '@teko/log';
import { formatCurrency } from '@teko/teko-ecommerce';
import {
  AddToCartType,
  TrackAddToCartParams,
  getTrackingData,
  trackAddToCart,
} from '@teko/tracking';
import { useCartV2, useProductDetailState, useTenantConfig } from 'contexts';
import { getEnv } from 'helpers';
import { AddToCartPayload } from 'hooks/useCalculatePrice';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { ConvertedProduct } from 'types';

export const BottomActionBar: React.FC = () => {
  const {
    product,
    price,
    isOutOfStock,
    actionReady,
    isAllowInstallmentPayment,
    isCanAddToCard,
    primitiveProduct,
    selectedPromotion,
    stockAvailable,
    selectedPromotionsNotApplyWithInstallment,
    showWarningPromotionWithInstallment,
    setShowWarningPromotionWithInstallment,
    minAmountByMonth,
    payloadAddToCart,
    setPayloadAddToCart,
    loadingProductDetailPage,
    setLoadingProductDetailPage,
  } = useProductDetailState();
  const {
    contactLink,
    allowBuyNow,
    isIdleCallbackTrackingAddToCart,
    isMobile,
    numberDisplayFormat,
  } = useTenantConfig();
  const showToast = useToast();
  const { onAddSingleItem } = useCartV2();
  const router = useRouter();

  const isValidPrice = useMemo(() => {
    if (!product) return false;
    if (price) return (price.finalPrice as number) >= 0;
  }, [product, price]);
  const showContact =
    !isValidPrice || isOutOfStock || !product?.status?.sellable;

  const onAddItemToCart = async (
    product: DiscoveryProduct,
    selectedPromotionId?: number,
    addToCartPrice?: number,
    isolatedCartType?: IsolatedCartType,
  ): Promise<void> => {
    if (!product) return;
    setLoadingProductDetailPage(true);
    const trackAddToCartParams: TrackAddToCartParams = {
      product: {
        sku: product?.productInfo.sellerSku || '',
        name: product?.productInfo.name || '',
        brand: product?.productInfo.brand,
        categories: product?.productInfo.categories,
        price: parseFloat(product?.prices?.[0].supplierRetailPrice),
        addToCartPrice: addToCartPrice as number,
        sellerSku: product?.productInfo.sellerSku,
      },
      selectedPromotionId,
      type:
        isolatedCartType === IsolatedCartType.BUY_NOW
          ? AddToCartType.BUYNOW
          : isolatedCartType === IsolatedCartType.INSTALLMENT
          ? AddToCartType.INSTALLMENT
          : AddToCartType.ADDTOCART,
    };
    if (!payloadAddToCart) {
      setLoadingProductDetailPage(false);
      return;
    }

    try {
      const res = await onAddSingleItem(
        {
          sku: payloadAddToCart?.sku,
          promotionId: selectedPromotionId,
          quantity: payloadAddToCart?.quantity,
          isPrimary: payloadAddToCart?.isPrimary,
        },
        false,
        isolatedCartType,
        { isSkipSetLoading: true },
      );
      if (res) {
        trackAddToCart(
          {
            ...trackAddToCartParams,
            status: 'success',
            cartId: res.id,
          },
          isIdleCallbackTrackingAddToCart,
        );
        if (!!isolatedCartType) {
          const key = getBuyNowKey(true);
          await router.push(`/checkout?bnk=${key}`);
        }
      } else {
        trackAddToCart(
          { ...trackAddToCartParams, status: 'failed' },
          isIdleCallbackTrackingAddToCart,
        );
      }
    } catch (e) {
      trackAddToCart(
        { ...trackAddToCartParams, status: 'failed' },
        isIdleCallbackTrackingAddToCart,
      );
      logger.error(e);
    } finally {
      setLoadingProductDetailPage(false);
    }
  };

  const onBuy = (): void => {
    if (isCanAddToCard && primitiveProduct) {
      onAddItemToCart(
        primitiveProduct,
        selectedPromotion?.promotionId,
        price?.finalPrice,
      );
    } else {
      showToast.info?.({
        message: t('Còn {{stockAvailable}} sản phẩm', {
          stockAvailable: stockAvailable,
        }),
      });
    }
  };

  const onBuyNow = () => {
    if (primitiveProduct) {
      onAddItemToCart(
        primitiveProduct,
        selectedPromotion?.promotionId,
        price?.finalPrice,
        IsolatedCartType.BUY_NOW,
      );
    }
  };

  const onBuyInstallment = () => {
    if (primitiveProduct) {
      onAddItemToCart(
        primitiveProduct,
        selectedPromotion?.promotionId,
        price?.finalPrice,
        IsolatedCartType.INSTALLMENT,
      );
    }
  };

  const handlePayOnInstallment = () => {
    if (selectedPromotionsNotApplyWithInstallment.length > 0) {
      return setShowWarningPromotionWithInstallment(true);
    }
    onBuyInstallment();
  };

  if (!product) return null;

  return (
    <>
      <Wrapper
        {...getTrackingData({
          isTrackContent: true,
          contentName: showContact && contactLink ? 'contactSale' : 'addToCart',
          target: 'productDetail',
          regionName: 'bottomProductDetail',
          payload: JSON.stringify({
            sku: product.sku,
            screenName: 'productDetail',
          }),
        })}
        isMobile={isMobile}
        marginTop={isMobile ? 's0' : 's12'}
      >
        <ProductInfo
          product={product}
          payloadAddToCart={payloadAddToCart}
          setPayloadAddToCart={setPayloadAddToCart}
        />
        <Flex
          align="center"
          justify="space-between"
          gap="s8"
          padding={isMobile ? ['s8', 's16', 's0', 's16'] : undefined}
          borderTop={isMobile ? '1px solid border' : undefined}
        >
          {showContact ? (
            <ContactButtonWrapper
              width="100%"
              {...getTrackingData({
                isTrackContent: true,
                contentName: 'contactSale',
                target: 'productDetail',
                regionName: 'bottomProductDetail',
                payload: JSON.stringify({
                  sku: product.sku,
                  screenName: 'productDetail',
                }),
              })}
            >
              <StyledButton
                width="100%"
                variant="primary"
                disabled={!contactLink && !actionReady}
                className={!contactLink ? 'out-of-stock-button' : ''}
                onClick={(): void => {
                  if (contactLink) window.open(contactLink);
                  return;
                }}
              >
                {contactLink ? t('Liên hệ') : t('Hết hàng')}
              </StyledButton>
            </ContactButtonWrapper>
          ) : !isAllowInstallmentPayment && !allowBuyNow ? (
            <Shape
              width="100%"
              {...getTrackingData({
                isTrackContent: true,
                contentName: 'addToCart',
                target: 'productDetail',
                regionName: 'bottomProductDetail',
                payload: JSON.stringify({
                  sku: product.sku,
                  screenName: 'productDetail',
                }),
              })}
            >
              <StyledButton
                width="100%"
                variant="primary"
                disabled={!actionReady || loadingProductDetailPage}
                onClick={() => onBuy()}
              >
                {t('Thêm vào giỏ hàng')}
              </StyledButton>
            </Shape>
          ) : (
            <Flex
              direction="row"
              width="100%"
              height="100%"
              align="center"
              justify="space-between"
              gap="s12"
            >
              <Flex
                height="48px"
                width="48px"
                borderRadius="s"
                border="1px solid primary"
                align="center"
                justify="center"
                onClick={onBuy}
                style={{
                  cursor: 'pointer',
                  opacity: loadingProductDetailPage ? 0.3 : 1,
                }}
              >
                <ShoppingCartAddColored size={18} />
              </Flex>
              {allowBuyNow && (
                <Shape
                  height="100%"
                  width={
                    isAllowInstallmentPayment
                      ? isMobile
                        ? 'calc((100% - 48px - 12px - 12px) / 2)'
                        : 'calc((100% - 48px - 12px - 12px) / 12 * 7)'
                      : 'calc(100% - 48px - 12px)'
                  }
                >
                  <StyledButton
                    width="100%"
                    variant="primary"
                    disabled={!actionReady}
                    onClick={() => onBuyNow()}
                  >
                    {t('Mua ngay')}
                  </StyledButton>
                </Shape>
              )}
              {isAllowInstallmentPayment && (
                <Shape
                  height="100%"
                  width={
                    isAllowInstallmentPayment
                      ? isMobile
                        ? 'calc((100% - 48px - 12px - 12px) / 2)'
                        : 'calc((100% - 48px - 12px - 12px) / 12 * 5)'
                      : 'calc(100% - 48px - 12px)'
                  }
                >
                  <Button
                    width="100%"
                    variant="primary"
                    disabled={!actionReady}
                    onClick={() => handlePayOnInstallment()}
                    backgroundColor="#4CB9CC"
                    height="48px"
                    padding="s4"
                  >
                    <Flex direction="column" align="center" justify="center">
                      <SubTitle color="#F7FEFF" level={2}>
                        {t('Trả góp')}
                      </SubTitle>
                      {typeof minAmountByMonth === 'number' && (
                        <Caption color="#F7FEFF" level={1}>
                          {`(${t('Chỉ từ')} ${formatCurrency(
                            minAmountByMonth,
                            numberDisplayFormat,
                          )})`}
                        </Caption>
                      )}
                    </Flex>
                  </Button>
                </Shape>
              )}
            </Flex>
          )}
        </Flex>
      </Wrapper>
      <Modal
        visible={showWarningPromotionWithInstallment}
        size="s"
        cancelText={t('Đóng')}
        onCancel={() => setShowWarningPromotionWithInstallment(false)}
        cancelButtonProps={{ style: { width: '100%' } }}
        okText={t('Tiến hành trả góp')}
        onOk={() => {
          setShowWarningPromotionWithInstallment(false);
          onBuyInstallment();
        }}
        okButtonProps={{ style: { width: '100%' } }}
        showCloseIcon={false}
        alignFooter="center"
      >
        <ModalHeader padding="s0">{t('Thông báo')}</ModalHeader>
        <ModalBody paddingLeft="s0" paddingRight="s0">
          {t(
            'Sản phẩm có khuyến mại không được áp dụng khi mua trả góp. Vui lòng chọn lại khuyến mại khác hoặc tiến hành trả góp mà không áp dụng khuyến mại',
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

type ProductInfoProps = {
  product: ConvertedProduct;
  payloadAddToCart?: AddToCartPayload;
  setPayloadAddToCart?: Dispatch<SetStateAction<AddToCartPayload>>;
};

const ProductInfo: React.FC<ProductInfoProps> = (props: ProductInfoProps) => {
  const { product, payloadAddToCart, setPayloadAddToCart } = props;
  const { colors } = useTheme();
  const { uomCodeToDisplayDecimalQuantity, isMobile } = useTenantConfig();
  const precision =
    product?.uomCode === uomCodeToDisplayDecimalQuantity ? 3 : 0;
  const selectedVariants = useMemo(() => {
    return product?.productOptions?.rows
      .map((variant) =>
        variant.options
          .filter((option) => option.selected)
          .map((option) => option.label),
      )
      .join(', ');
  }, [product?.productOptions?.rows]);

  return (
    <Flex
      align="center"
      justify={isMobile ? 'space-between' : 'start'}
      padding={isMobile ? ['s8', 's16'] : 's0'}
      gap={isMobile ? 's8' : 's16'}
    >
      <Row style={{ minWidth: 0 }} wrap={false} align="middle">
        {isMobile ? (
          <>
            <Col style={{ minWidth: 40 }}>
              <Image
                width={40}
                height={40}
                src={product?.images?.[0]?.src || ''}
                alt={product?.images?.[0]?.alt || 'product-image-alt'}
              />
            </Col>
            <Col style={{ padding: '0 8px', minWidth: 0 }}>
              <EllipsisText style={{ fontWeight: 'bold', fontSize: '15px' }}>
                {product?.displayName}
              </EllipsisText>
              {product?.productGroup?.variants?.length ? (
                <EllipsisText style={{ color: colors.textSecondary }}>
                  <span>{t('Phân loại')}: </span>
                  <span>{selectedVariants}</span>
                </EllipsisText>
              ) : null}
            </Col>
          </>
        ) : (
          <>
            <Body level={1}>{t('Số lượng')}</Body>
          </>
        )}
      </Row>
      <QuantityBlock quantity={payloadAddToCart?.quantity || 1}>
        <Quantity
          className="quantity-selector"
          disableMinus={payloadAddToCart?.quantity === 1}
          quantity={payloadAddToCart?.quantity || 1}
          setQuantity={(value) => {
            if (value !== payloadAddToCart?.quantity) {
              setPayloadAddToCart?.((prev) => {
                return {
                  ...prev,
                  quantity: value,
                };
              });
            }
          }}
          onPlus={() => {
            setPayloadAddToCart?.((prev) => {
              return {
                ...prev,
                quantity: (prev.quantity || 1) + 1,
              };
            });
          }}
          onMinus={() => {
            if ((payloadAddToCart?.quantity || 1) - 1 > 0) {
              setPayloadAddToCart?.((prev) => {
                return {
                  ...prev,
                  quantity: (prev.quantity || 1) - 1,
                };
              });
            }
          }}
          min={Math.pow(10, -precision)}
          {...(getEnv('EPIC_ECOM_489') !== 'enabled'
            ? { decimalSeparator: '.' }
            : {})}
          precision={precision}
          blurOnEnter
          color="textTitle"
        />
      </QuantityBlock>
    </Flex>
  );
};

interface WrapperProps extends BaseProps {
  isMobile?: boolean;
}

const Wrapper = styled(Shape)<WrapperProps>(
  ({ isMobile }: WrapperProps) => `
  padding-bottom: env(safe-area-inset-bottom);
  min-height: 72px;
  position: ${isMobile ? 'sticky' : 'relative'};
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  z-index: 1030;
  display: flex;
  flex-direction: column;
  gap: ${isMobile ? '8px' : '12px'};
  padding-bottom: ${isMobile ? '24px' : '0px'};
  border-top: ${isMobile ? '1px solid #ddd' : ''};
`,
);
const ContactButtonWrapper = styled(Shape)`
  .out-of-stock-button {
    background-color: #dcdee9;
    .button-text {
      color: #a7abc3;
    }
  }
`;

const StyledButton = styled(Button)`
  height: 48px;
  .button-text {
    text-transform: uppercase;
    font-weight: 500;
    font-size: 15px;
    line-height: 24px;
  }
`;

const EllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuantityBlock = styled.div<{ quantity: number }>`
  display: flex;
  align-items: center;
  .quantity-selector {
    width: 132px !important;
    height: 40px !important;
    .rc-input-number-input {
      border-left: 1px solid ${({ theme }) => theme.colors.border};
      border-right: 1px solid ${({ theme }) => theme.colors.border};
      background-color: white;
      font-size: 15px;
      font-weight: 500;
      line-height: 24px;
      white-space: nowrap;
    }
    border: 1px solid rgb(228, 229, 240);
  }
  .plus-btn {
    background-color: white;
  }
  ${({ quantity }) =>
    quantity > 1 &&
    `
    .minus-btn {
      background-color: white;
    }
  `}
`;
